import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Sparkles, FlaskConical, RefreshCw } from "lucide-react";

export const fmt = (n) => {
  if (n == null) return "—";
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return n.toLocaleString();
};

export function DemoBadge({ className = "" }) {
  return (
    <span data-testid="analytics-demo-badge" className={`inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ${className}`}>
      <FlaskConical className="h-3.5 w-3.5" /> Demo data — connect Instagram for live stats
    </span>
  );
}

export function DeltaPill({ delta }) {
  if (delta == null || delta === 0) return null;
  const up = delta > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold ${up ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
      {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
      {Math.abs(delta)}%
    </span>
  );
}

export function StatCard({ label, metric, suffix = "", Icon, tint = "text-emerald-600 bg-emerald-100", index = 0, testid }) {
  const val = metric?.isRate ? `${metric.value}${suffix || "%"}` : `${fmt(metric?.value)}${suffix}`;
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05, duration: 0.35 }}
      className="rounded-2xl border border-zinc-200 bg-white p-4 sm:p-5">
      <div className="flex items-center justify-between">
        {Icon && <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${tint}`}><Icon className="h-5 w-5" /></div>}
        <DeltaPill delta={metric?.delta} />
      </div>
      <p className="mt-3 font-display text-2xl font-bold tracking-tight text-zinc-900 sm:text-3xl">{val}</p>
      <p className="mt-0.5 text-xs font-medium text-zinc-500">{label}</p>
    </motion.div>
  );
}

export function SectionCard({ title, subtitle, right, children, testid, className = "" }) {
  return (
    <div data-testid={testid} className={`overflow-hidden rounded-3xl border border-zinc-200 bg-white p-5 sm:p-6 ${className}`}>
      {(title || right) && (
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            {title && <h3 className="font-display text-lg font-bold tracking-tight text-zinc-900">{title}</h3>}
            {subtitle && <p className="mt-0.5 text-sm text-zinc-500">{subtitle}</p>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function AiInsight({ insights = [] }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (insights.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % insights.length), 6000);
    return () => clearInterval(t);
  }, [insights.length]);
  if (!insights.length) return null;
  return (
    <div data-testid="ai-insight-card" className="relative overflow-hidden rounded-3xl border border-indigo-200 bg-gradient-to-br from-indigo-600 via-violet-600 to-emerald-600 p-5 text-white sm:p-6">
      <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 backdrop-blur"><Sparkles className="h-4.5 w-4.5" /></div>
        <p className="font-display text-sm font-bold tracking-wide">Nearfluence AI Insight</p>
        <span className="ml-1 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Beta</span>
        {insights.length > 1 && (
          <button data-testid="ai-insight-next" onClick={() => setI((x) => (x + 1) % insights.length)} className="ml-auto flex h-7 w-7 items-center justify-center rounded-full bg-white/15 transition-colors hover:bg-white/25">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <motion.p key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        data-testid="ai-insight-text" className="mt-3 font-display text-lg font-semibold leading-snug sm:text-xl">
        “{insights[i]}”
      </motion.p>
      <div className="mt-4 flex gap-1.5">
        {insights.map((_, x) => (
          <span key={x} className={`h-1.5 rounded-full transition-all ${x === i ? "w-6 bg-white" : "w-1.5 bg-white/40"}`} />
        ))}
      </div>
    </div>
  );
}

export function Tabs({ tabs, active, onChange }) {
  return (
    <div className="flex gap-2 overflow-x-auto no-scrollbar">
      {tabs.map((t) => (
        <button key={t.key} data-testid={`analytics-tab-${t.key}`} onClick={() => onChange(t.key)}
          className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${active === t.key ? "bg-zinc-900 text-white" : "border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"}`}>
          {t.Icon && <t.Icon className="h-4 w-4" />} {t.label}
        </button>
      ))}
    </div>
  );
}
